import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Topbar from '../components/Topbar';
import { getFlightDetails, deleteManualFlight, updateFlightStatus } from '../services/flightOperationsService';
import Spinner from '../components/Spinner';
import { toast } from 'react-toastify';
import boardingClockIcon from '../assets/icons/boarding-clock.png';
import gateIconImg from '../assets/icons/gate-icon.png';
import terminalIconImg from '../assets/icons/terminal-icon.png';
import seatIconImg from '../assets/icons/seat-icon.png';
import routePlaneIcon from '../assets/icons/route-plane.png';
import statusPlanePhoto from '../assets/icons/status-plane.png';

const Svg = ({ d, ...props }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d={d} />
  </svg>
);

const getLogoUrl = (logoPath) => {
  if (!logoPath) return null;
  if (logoPath.startsWith('http://') || logoPath.startsWith('https://')) {
    return logoPath;
  }
  const baseUrl = process.env.REACT_APP_IMG_BASE_URL || 'http://localhost:3001/';
  const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  const cleanPath = logoPath.startsWith('/') ? logoPath : `/${logoPath}`;
  return `${cleanBase}${cleanPath}`;
};

const getStatusBadgeStyle = (status) => {
  const s = status?.toLowerCase() || '';
  if (s.includes('time') || s.includes('scheduled') || s.includes('landed') || s.includes('departed')) {
    return { bg: '#EAFAF3', color: '#1A7A42', border: '1px solid #A8DDB5' };
  }
  if (s.includes('delay')) {
    return { bg: '#FFF4E0', color: '#B8860B', border: '1px solid #F5CBA7' };
  }
  if (s.includes('cancel')) {
    return { bg: '#FFF0F0', color: '#E53E3E', border: '1px solid #FFD5D5' };
  }
  return { bg: '#EEF6FF', color: 'var(--blue)', border: '1px solid #B3D9FF' };
};

const formatTime = (timeVal) => {
  if (!timeVal || timeVal === '--:--' || timeVal === '—') return null;
  if (typeof timeVal === 'string' && (timeVal.includes('AM') || timeVal.includes('PM'))) return timeVal;
  try {
    const d = new Date(timeVal);
    if (isNaN(d.getTime())) return timeVal;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return timeVal;
  }
};

const formatDate = (dateVal) => {
  if (!dateVal || dateVal === '—') return null;
  try {
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return dateVal;
    return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' });
  } catch {
    return dateVal;
  }
};

const getTimelineData = (details) => {
  const flight = details?.flight || {};
  const card = details?.card_details || {};
  const route = card.route_overview || {};
  const boarding = card.boarding_info || {};
  const dep = flight.departure || {};
  const arr = flight.arrival || {};

  const statusRaw = (card.flight_status_card?.status || card.header?.status || flight.status || 'scheduled').toString().toLowerCase();

  const originIata = route.origin_iata || dep.iata || flight.origin_iata || flight.origin_airport || 'Origin';
  const destIata = route.destination_iata || arr.iata || flight.destination_iata || flight.destination_airport || 'Destination';

  // Step 1: Scheduled
  const schedTime = formatTime(route.departs_time) || formatTime(dep.scheduled_time) || formatTime(flight.scheduled_time);
  const schedDate = formatDate(route.departs_date) || formatDate(dep.scheduled_time) || formatDate(flight.scheduled_time) || formatDate(flight.flight_date);

  // Step 2: Boarding
  const boardTime = formatTime(boarding.boarding_time) || formatTime(flight.boarding_time) || schedTime;
  const boardDate = formatDate(boarding.boarding_date) || formatDate(flight.boarding_time) || schedDate;
  const gateVal = boarding.gate || dep.gate || flight.gate;
  const boardLoc = gateVal && gateVal !== 'TBD' ? `Gate ${gateVal}` : originIata;

  // Step 3: In Air
  const inAirTime = formatTime(dep.actual_time) || formatTime(dep.estimated_time) || (route.duration ? `${route.duration}` : (flight.duration ? `${flight.duration}m` : null));
  const inAirDate = statusRaw.includes('depart') || statusRaw.includes('air') || statusRaw.includes('active') || statusRaw.includes('en-route') ? 'In Transit' : null;
  const inAirLoc = `${originIata} ➔ ${destIata}`;

  // Step 4: Landing
  const landTime = formatTime(route.arrives_time) || formatTime(arr.estimated_time) || formatTime(arr.scheduled_time);
  const landDate = formatDate(route.arrives_date) || formatDate(arr.estimated_time) || formatDate(arr.scheduled_time);

  // Step 5: Completed
  const completeTime = formatTime(arr.actual_time) || landTime;
  const completeDate = formatDate(arr.actual_time) || landDate || (statusRaw.includes('land') || statusRaw.includes('complete') || statusRaw.includes('arrive') ? 'Landed' : null);

  let activeIndex = 0;
  let isDelayed = false;
  let isCancelled = false;

  if (statusRaw.includes('cancel')) {
    isCancelled = true;
    activeIndex = 0;
  } else if (statusRaw.includes('delay')) {
    isDelayed = true;
    activeIndex = 0;
  } else if (statusRaw.includes('board')) {
    activeIndex = 1;
  } else if (statusRaw.includes('depart') || statusRaw.includes('air') || statusRaw.includes('active') || statusRaw.includes('en-route') || statusRaw.includes('en route')) {
    activeIndex = 2;
  } else if (statusRaw.includes('land')) {
    activeIndex = 3;
  } else if (statusRaw.includes('complete') || statusRaw.includes('arrive')) {
    activeIndex = 4;
  } else {
    activeIndex = 0;
  }

  const steps = [
    { id: 'scheduled', title: 'Scheduled', time: schedTime, date: schedDate, location: originIata, iconType: 'calendar' },
    { id: 'boarding', title: 'Boarding', time: boardTime, date: boardDate, location: boardLoc, iconType: 'boarding' },
    { id: 'in_air', title: 'In Air', time: inAirTime, date: inAirDate, location: inAirLoc, iconType: 'plane' },
    { id: 'landing', title: 'Landing', time: landTime, date: landDate, location: destIata, iconType: 'landing' },
    { id: 'completed', title: 'Completed', time: completeTime, date: completeDate, location: destIata, iconType: 'check' }
  ];

  return { steps, activeIndex, isDelayed, isCancelled };
};

const getInsightColor = (val) => {
  if (!val || val === 'N/A' || val === '—') return '#64748B';
  const v = val.toLowerCase();
  if (v.includes('low') || v.includes('short') || v.includes('at gate') || v.includes('on time') || v.includes('open')) return '#16A34A';
  if (v.includes('med') || v.includes('pre-board') || v.includes('boarding')) return '#D97706';
  if (v.includes('high') || v.includes('long') || v.includes('closed') || v.includes('full') || v.includes('delay')) return '#DC2626';
  return '#2563EB';
};

const FlightDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      try {
        const response = await getFlightDetails(id);
        if (response && (response.data || response.flight || response.card_details)) {
          setDetails(response.data || response);
        } else {
          toast.error("Flight details not found");
        }
      } catch (error) {
        console.error(error);
        toast.error("Failed to load flight details");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDetails();
  }, [id]);

  const handleDeleteFlight = async () => {
    if (window.confirm('Are you sure you want to permanently delete this flight? This action cannot be undone.')) {
      try {
        const response = await deleteManualFlight(id);
        if (response && (response.status === 200 || response.status === 204 || !response.error)) {
          toast.success("Flight deleted successfully");
          navigate('/flights');
        } else {
          toast.error("Failed to delete flight");
        }
      } catch (error) {
        console.error(error);
        toast.error("Error deleting flight");
      }
    }
  };

  const handleEditFlight = async () => {
    const flightStatus = details?.flight?.status || details?.card_details?.header?.status || 'scheduled';
    const newStatus = window.prompt("Enter new flight status (e.g., scheduled, active, landed, delayed, cancelled):", flightStatus);
    if (newStatus && newStatus !== flightStatus) {
      try {
        const payload = { status: newStatus };
        const response = await updateFlightStatus(id, payload);
        if (response && (response.status === 200 || response.status === 204 || !response.error)) {
          toast.success("Flight status updated");
          window.location.reload();
        } else {
          toast.error("Failed to update flight");
        }
      } catch (error) {
        console.error(error);
        toast.error("Error updating flight");
      }
    }
  };

  if (loading) return <Spinner />;

  const flight = details?.flight || {};
  const card = details?.card_details || {};
  const header = card.header || {};
  const route = card.route_overview || {};
  const boarding = card.boarding_info || {};
  const flightStatus = card.flight_status_card || {};
  const travelerUpdates = card.traveler_updates || details?.traveler_updates || flight.traveler_updates || {};

  const statusStyle = getStatusBadgeStyle(header.status || flight.status || 'Scheduled');
  const { steps, activeIndex, isDelayed, isCancelled } = getTimelineData(details);
  const gateName = boarding.gate || flight.gate || flight.departure?.gate || '';

  const rawLogo = header.airline_logo || header.logo || flight.airline?.logo || details?.airline?.logo;
  const logoUrl = getLogoUrl(rawLogo);

  return (
    <div>
      <Topbar title="Flight Details" subtitle="Real-time status overview and traveler updates" />

      <div className="content" style={{ paddingBottom: 40, width: '100%', margin: '0 auto' }}>
        
        {/* TOP HEADER & ACTIONS */}
        <div className="fd-top-actions">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--blue)', fontWeight: 600, flexWrap: 'wrap' }}>
            <span
              onClick={() => navigate('/flights')}
              style={{ cursor: 'pointer', color: 'var(--blue)' }}
            >
              Flight Operations
            </span>
            <span style={{ color: 'var(--grey)', fontWeight: 400 }}>·</span>
            <span style={{ color: 'var(--grey)', fontWeight: 400 }}>
              {header.flight_number || flight.flight_number || `Flight #${id}`}
            </span>
          </div>

          <button
            className="btn btn-outline"
            onClick={() => navigate('/flights')}
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, background: 'white' }}
          >
            <Svg d="M19 12H5M12 19l-7-7 7-7" />
            Back to List
          </button>
        </div>

        {/* TOP CARD (Merged Airline Header + Route Overview) */}
        <div className="fd-top-card">
          {/* Airline Header */}
          <div className="fd-airline-section">
            <div style={{
              width: 72, height: 72, borderRadius: '50%',
              background: logoUrl && !imgError ? '#FFFFFF' : '#0078D2',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: 24, fontWeight: 800,
              overflow: 'hidden', border: '1.5px dashed #CBD5E1', padding: 8, flexShrink: 0
            }}>
              {logoUrl && !imgError ? (
                <img src={logoUrl} alt={header.airline_name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} onError={() => setImgError(true)} />
              ) : (flight.airline?.iata || header.flight_number?.substring(0, 2) || 'AA')}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <h2 style={{ fontSize: 28, fontWeight: 800, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>
                  {header.flight_number || flight.flight_number || id}
                </h2>
                <span style={{
                  padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                  background: statusStyle.bg, color: statusStyle.color, border: statusStyle.border, whiteSpace: 'nowrap'
                }}>{header.status || flight.status || 'Scheduled'}</span>
              </div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--dark)', marginTop: 4 }}>
                {header.airline_name || 'Airline'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--grey)', marginTop: 2 }}>
                {header.operated_by || `Operated by ${header.airline_name || 'Airline'}`}
              </div>
            </div>
          </div>

          <div className="fd-divider" />

          {/* Route Overview */}
          <div className="fd-route-overview">
            <div style={{ textAlign: 'center', minWidth: 80, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--blue)', fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1 }}>
                {route.origin_iata || '—'}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', marginTop: 6 }}>
                {route.origin_city || 'Origin'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2, whiteSpace: 'normal' }}>
                {route.origin_airport || 'Airport'}
              </div>
            </div>

            <div style={{ flex: 1, padding: '0 12px', textAlign: 'center', minWidth: 100 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#64748B', marginBottom: 6 }}>
                {route.duration || (flight.duration ? `${flight.duration}m` : 'In Air')}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', border: '1.5px solid #CBD5E1', background: 'white', flexShrink: 0 }} />
                <div style={{ height: 2, flex: 1, borderTop: '2px dashed #94A3B8', minWidth: 10 }} />
                <img src={routePlaneIcon} alt="Plane" style={{ width: 20, height: 20, flexShrink: 0 }} />
                <div style={{ height: 2, flex: 1, borderTop: '2px dashed #94A3B8', minWidth: 10 }} />
                <div style={{ width: 6, height: 6, borderRadius: '50%', border: '1.5px solid #CBD5E1', background: 'white', flexShrink: 0 }} />
              </div>
            </div>

            <div style={{ textAlign: 'center', minWidth: 80, flex: 1 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: 'var(--blue)', fontFamily: "'Space Grotesk', sans-serif", lineHeight: 1 }}>
                {route.destination_iata || '—'}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--dark)', marginTop: 6 }}>
                {route.destination_city || 'Destination'}
              </div>
              <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2, whiteSpace: 'normal' }}>
                {route.destination_airport || 'Airport'}
              </div>
            </div>
          </div>
          
          <div className="fd-divider" />

          {/* Times */}
          <div className="fd-times-section">
             <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--grey)', letterSpacing: '0.5px' }}>DEPARTS</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)', marginTop: 4 }}>{route.departs_time || '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2 }}>{route.departs_date || ''}</div>
              </div>

              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--grey)', letterSpacing: '0.5px' }}>ARRIVES</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)', marginTop: 4 }}>{route.arrives_time || '—'}</div>
                <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 2 }}>{route.arrives_date || ''}</div>
              </div>
          </div>

        </div>

        {/* MAIN GRID */}
        <div className="fd-main-grid">
           {/* LEFT COLUMN */}
           <div className="fd-col">
             
             {/* BOARDING INFORMATION */}
             <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
               <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
                 <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4F46E5' }}>
                   <Svg d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                 </div>
                 <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>Boarding Information</h3>
               </div>

               <div className="fd-boarding-grid">
                 <div style={{ textAlign: 'center' }}>
                   <img src={boardingClockIcon} alt="Boarding" style={{ width: 24, height: 24, marginBottom: 8 }} />
                   <div style={{ fontSize: 12, color: 'var(--dark)' }}>Boarding</div>
                   <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>{boarding.boarding_time || '—'}</div>
                   <div style={{ fontSize: 11, color: 'var(--grey)' }}>{boarding.boarding_date || ''}</div>
                 </div>
                 <div style={{ textAlign: 'center', borderLeft: '1px solid #F1F5F9' }}>
                   <img src={gateIconImg} alt="Gate" style={{ width: 24, height: 24, marginBottom: 8 }} />
                   <div style={{ fontSize: 12, color: 'var(--dark)' }}>Gate</div>
                   <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>{boarding.gate || '—'}</div>
                   <div style={{ fontSize: 11, color: 'var(--grey)' }}>{boarding.concourse || ''}</div>
                 </div>
                 <div style={{ textAlign: 'center', borderLeft: '1px solid #F1F5F9' }}>
                   <img src={terminalIconImg} alt="Terminal" style={{ width: 24, height: 24, marginBottom: 8 }} />
                   <div style={{ fontSize: 12, color: 'var(--dark)' }}>Terminal</div>
                   <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>{boarding.terminal || '—'}</div>
                   <div style={{ fontSize: 11, color: 'var(--grey)' }}>{boarding.terminal_subtext || ''}</div>
                 </div>
                 <div style={{ textAlign: 'center', borderLeft: '1px solid #F1F5F9' }}>
                   <img src={seatIconImg} alt="Seat" style={{ width: 24, height: 24, marginBottom: 8 }} />
                   <div style={{ fontSize: 12, color: 'var(--dark)' }}>Seat</div>
                   <div style={{ fontSize: 16, fontWeight: 800, color: 'var(--navy)', fontFamily: "'Space Grotesk', sans-serif" }}>{boarding.seat || '—'}</div>
                   <div style={{ fontSize: 11, color: 'var(--grey)' }}>Window</div>
                 </div>
               </div>

               <div style={{ background: '#F0F7FF', borderRadius: 12, padding: '12px 16px', display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 12, color: 'var(--blue)', fontWeight: 600 }}>
                 <div style={{ flexShrink: 0 }}>
                   <Svg d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                 </div>
                 <span>Boarding group 3 - Arrive at gate with enough time.</span>
               </div>
             </div>

             {/* FLIGHT TIMELINE */}
             <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.03)', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
                  <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4F46E5' }}>
                    <Svg d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </div>
                  <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>Flight Timeline</h3>
                </div>

                <div className="fd-timeline">
                  {steps.map((step, idx) => {
                    const isPast = idx < activeIndex;
                    const isActive = idx === activeIndex;
                    const isFuture = idx > activeIndex;

                    let circleBg = 'white';
                    let circleBorder = '2px solid #E2E8F0';
                    let circleColor = '#94A3B8';
                    let titleColor = 'var(--grey)';

                    if (isPast) {
                      circleBg = '#22C55E';
                      circleBorder = '2px solid #22C55E';
                      circleColor = 'white';
                      titleColor = 'var(--navy)';
                    } else if (isActive) {
                      if (isCancelled) {
                        circleBg = '#FEF2F2';
                        circleBorder = '2px solid #EF4444';
                        circleColor = '#EF4444';
                        titleColor = '#EF4444';
                      } else if (isDelayed) {
                        circleBg = '#FFFBEB';
                        circleBorder = '2px solid #F59E0B';
                        circleColor = '#D97706';
                        titleColor = '#D97706';
                      } else {
                        circleBg = '#EFF6FF';
                        circleBorder = '2px solid #3B82F6';
                        circleColor = '#3B82F6';
                        titleColor = '#3B82F6';
                      }
                    }

                    let lineBorder = '2px dashed #E2E8F0';
                    if (idx < activeIndex) {
                      lineBorder = '2px solid #3B82F6';
                    } else if (idx === activeIndex && isDelayed) {
                      lineBorder = '2px dashed #F59E0B';
                    } else if (idx === activeIndex && isCancelled) {
                      lineBorder = '2px dashed #EF4444';
                    }

                    return (
                      <React.Fragment key={step.id}>
                        <div style={{ textAlign: 'center', width: 75 }}>
                          <div style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: circleBg, border: circleBorder,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            margin: '0 auto 8px', color: circleColor,
                            boxShadow: isActive ? '0 0 0 4px rgba(59, 130, 246, 0.15)' : 'none'
                          }}>
                            {isPast ? (
                              <Svg d="M5 13l4 4L19 7" stroke="white" />
                            ) : step.iconType === 'calendar' ? (
                              <Svg d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            ) : step.iconType === 'boarding' ? (
                              <Svg d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            ) : step.iconType === 'plane' ? (
                              <img src={routePlaneIcon} style={{ width: 16, height: 16, opacity: isFuture ? 0.5 : 1 }} alt="" />
                            ) : step.iconType === 'landing' ? (
                              <Svg d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                            ) : (
                              <Svg d="M5 13l4 4L19 7" />
                            )}
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: titleColor }}>{step.title}</div>
                          <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 4, lineHeight: 1.3 }}>
                            {step.time && <div>{step.time}</div>}
                            {step.date && <div>{step.date}</div>}
                            {step.location && <div>{step.location}</div>}
                          </div>
                        </div>

                        {idx < steps.length - 1 && (
                          <div style={{ flex: 1, height: 2, borderTop: lineBorder, margin: '0 4px', alignSelf: 'flex-start', marginTop: 18 }} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
             </div>

           </div>

           {/* RIGHT COLUMN */}
           <div className="fd-col">
             
             {/* FLIGHT STATUS */}
             <div className="fd-status-card">
               <div>
                   <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                     <div style={{ width: 36, height: 36, borderRadius: 10, background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4F46E5', flexShrink: 0 }}>
                       <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                         <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 00-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" />
                       </svg>
                     </div>
                     <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>Flight Status</h3>
                   </div>
                  <span style={{
                    display: 'inline-flex', padding: '4px 12px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                    background: statusStyle.bg, color: statusStyle.color, border: statusStyle.border, marginBottom: 12
                  }}>
                    {flightStatus.status || header.status || flight.status || 'On Time'}
                  </span>
                  <div style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 600 }}>
                    {flightStatus.description || 'The flight is currently on schedule.'}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--grey)', marginTop: 4 }}>
                    {flightStatus.last_updated || 'Last updated: 2 minutes ago'}
                  </div>
               </div>
               
               <div className="fd-status-img" style={{ width: 140, height: 90, borderRadius: 12, overflow: 'hidden', flexShrink: 0 }}>
                 <img src={statusPlanePhoto} alt="Airplane" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
               </div>
             </div>

             {/* LIVE FROM TRAVELERS */}
             <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.03)', minHeight: '203px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4F46E5' }}>
                      <Svg d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                    </div>
                    <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--navy)' }}>
                      Live from travelers {gateName ? `at Gate ${gateName}` : ''}
                    </h3>
                  </div>
                  <Svg d="M9 5l7 7-7 7" stroke="var(--grey)" />
                </div>
                <div style={{ fontSize: 12, color: 'var(--grey)', marginBottom: 20 }}>
                  {travelerUpdates.last_updated ? `Real-time traveler updates (${travelerUpdates.last_updated})` : (travelerUpdates.traveler_updates_count ? `${travelerUpdates.traveler_updates_count} traveler updates reported` : 'Real-time traveler updates')}
                </div>
                
                <div className="fd-traveler-grid">
                  <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--grey)', marginBottom: 6 }}>Walk to gate</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: getInsightColor(travelerUpdates.line_for_agent || travelerUpdates.line_at_gate) }}>
                      {travelerUpdates.line_for_agent || travelerUpdates.line_at_gate || 'N/A'}
                    </div>
                  </div>
                  <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--grey)', marginBottom: 6 }}>Crowd Level</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: getInsightColor(travelerUpdates.crowd_level) }}>
                      {travelerUpdates.crowd_level || 'N/A'}
                    </div>
                  </div>
                  <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--grey)', marginBottom: 6 }}>Boarding Status</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: getInsightColor(travelerUpdates.boarding_status) }}>
                      {travelerUpdates.boarding_status || 'N/A'}
                    </div>
                  </div>
                  <div style={{ background: '#F8FAFC', borderRadius: 10, padding: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: 'var(--grey)', marginBottom: 6 }}>Pace of Gate</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: getInsightColor(travelerUpdates.plane_at_gate) }}>
                      {travelerUpdates.plane_at_gate || 'N/A'}
                    </div>
                  </div>
                </div>
             </div>

             {/* FLIGHT NOTES - hidden for now */}
             {false && (
               <div style={{ background: 'white', borderRadius: 16, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', flex: 1 }}>
                 <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                   <div style={{ width: 28, height: 28, borderRadius: '50%', background: '#EEF2FF', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4F46E5' }}>
                     <Svg d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                   </div>
                   <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>Flight Notes</h3>
                 </div>
                 
                 <textarea 
                   placeholder="Add notes about this flight..."
                   style={{ width: '100%', flex: 1, minHeight: 120, background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 12, padding: 16, fontSize: 13, resize: 'none', color: 'var(--dark)' }}
                 ></textarea>
                 
                 <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                   <button className="btn btn-primary" style={{ padding: '8px 20px', borderRadius: 8, fontSize: 13 }}>Save Notes</button>
                 </div>
               </div>
             )}
             
           </div>
        </div>

        <style>{`
          /* TOP ACTIONS */
          .fd-top-actions {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            gap: 16px;
          }
          /* TOP CARD */
          .fd-top-card {
            background: white;
            border-radius: 16px;
            padding: 24px 32px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.03);
            display: flex;
            align-items: center;
            gap: 40px;
            margin-bottom: 20px;
            flex-wrap: wrap;
          }
          .fd-airline-section {
            display: flex;
            align-items: center;
            gap: 20px;
            min-width: 320px;
          }
          .fd-divider {
            width: 1px;
            height: 80px;
            background: #F1F5F9;
          }
          .fd-route-overview {
            flex: 1;
            display: flex;
            justify-content: space-between;
            align-items: center;
            min-width: 250px;
          }
          .fd-times-section {
            display: flex;
            gap: 40px;
            min-width: 200px;
            justify-content: flex-end;
          }
          /* STATUS CARD */
          .fd-status-card {
            background: white;
            border-radius: 16px;
            padding: 24px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.03);
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }
          /* MAIN GRID */
          .fd-main-grid {
            display: grid;
            grid-template-columns: 1.2fr 1fr;
            gap: 20px;
            align-items: stretch;
          }
          .fd-col {
            display: flex;
            flex-direction: column;
            gap: 20px;
          }
          /* BOARDING GRID */
          .fd-boarding-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
            margin-bottom: 24px;
          }
          /* TIMELINE */
          .fd-timeline {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: 0 10px;
            overflow-x: auto;
          }
          /* TRAVELER GRID */
          .fd-traveler-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
          }

          @media (max-width: 1100px) {
            .fd-top-card {
              gap: 20px;
              padding: 20px;
            }
            .fd-airline-section {
              min-width: 260px;
            }
          }

          @media (max-width: 900px) {
            .fd-main-grid {
              grid-template-columns: 1fr;
            }
            .fd-divider {
              display: none;
            }
            .fd-top-card {
              flex-direction: column;
              align-items: stretch;
              gap: 20px;
            }
            .fd-airline-section {
              min-width: auto;
            }
            .fd-times-section {
              justify-content: space-between;
            }
          }

          @media (max-width: 600px) {
            .fd-top-actions {
              flex-direction: column;
              align-items: flex-start;
            }
            .fd-boarding-grid {
              grid-template-columns: repeat(2, 1fr);
            }
            .fd-traveler-grid {
              grid-template-columns: repeat(2, 1fr);
            }
            .fd-timeline {
              padding: 0;
              gap: 4px;
            }
            .fd-timeline > div {
              width: 54px !important;
            }
            .fd-status-card {
              flex-direction: column-reverse;
              gap: 16px;
            }
            .fd-status-img {
              width: 100% !important;
              height: 140px !important;
            }
          }
        `}</style>

      </div>
    </div>
  );
};

export default FlightDetails;
